/**
 * The checks that did not run, and why.
 *
 * A QC pass or an export preflight with few findings is only reassuring
 * if the checks that had nothing to compare against say so. Without
 * this, "clean because nothing was wrong" and "clean because half the
 * checks never ran" look identical. Collapsed by default: it is there
 * to be found by anyone who asks "did it check X?", not to compete with
 * the findings.
 */
import React from 'react';
import type { SkippedCheck } from '@/types';

const KIND_LABEL: Record<SkippedCheck['kind'], string> = {
  missing_input: 'nothing to check against',
  not_applicable: 'does not apply',
  error: 'failed to run',
};

export default function SkippedChecksNote({
  skipped, testId = 'skipped-checks',
}: {
  skipped: SkippedCheck[] | null | undefined;
  testId?: string;
}): JSX.Element | null {
  if (!skipped || skipped.length === 0) return null;
  const failed = skipped.filter((s) => s.kind === 'error').length;
  return (
    <details className="text-[11px] text-zinc-600" data-testid={testId}>
      <summary className="cursor-pointer select-none">
        {skipped.length} check{skipped.length === 1 ? '' : 's'} did not run
        {failed > 0 && (
          <span className="text-rose-700">
            {' '}({failed} failed)
          </span>
        )}
      </summary>
      <ul className="mt-1 ml-4 list-disc space-y-0.5">
        {skipped.map((s, i) => (
          <li key={`${s.check}-${i}`} data-testid={`${testId}-${s.check}`}>
            <span className="font-mono">{s.check}</span>
            {' — '}
            <span className={s.kind === 'error' ? 'text-rose-700' : 'text-zinc-500'}>
              {KIND_LABEL[s.kind] || s.kind}
            </span>
            {': '}
            {s.reason}
          </li>
        ))}
      </ul>
    </details>
  );
}
